import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Grid, TextField, MenuItem,
    Select, FormControl, InputLabel, RadioGroup, FormControlLabel,
    Radio, Button, Divider, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Alert, Snackbar, Stack,
    CircularProgress, InputAdornment,
} from '@mui/material';
import {
    FileDownload as ExportIcon,
    CalendarViewWeek as WeekIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { axiosInstance, getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

const MONTHS = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

const fmtRange = (w) => {
    const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    return `${fmt(w.weekStart)} - ${fmt(w.weekEnd)}`;
};

export default function TimesheetReportsExport({ refreshKey }) {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData?.organization_id;

    // Shared filters — drive the employee weekly table
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(CURRENT_YEAR);
    const [departments, setDepartments] = useState([]);
    const [dept, setDept] = useState('All');
    const [search, setSearch] = useState('');

    // Employee weekly summary — payroll/invoicing input
    const [weeklyData, setWeeklyData] = useState(null);
    const [weeklyLoading, setWeeklyLoading] = useState(false);

    // Export filters (independent — separate date range, not tied to month/year above)
    const [exportFormat,   setExportFormat]   = useState('excel');
    const [exportFromDate, setExportFromDate] = useState('');
    const [exportToDate,   setExportToDate]   = useState('');
    const [exportStatus,   setExportStatus]   = useState('All');
    const [exportDept,     setExportDept]     = useState('All');
    const [exporting,      setExporting]      = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

    useEffect(() => {
        if (!orgId) return;
        const deptUrl = typeof API_ENDPOINTS.fetchDepartmentsByOrganizationId === 'function'
            ? API_ENDPOINTS.fetchDepartmentsByOrganizationId(orgId)
            : `${API_ENDPOINTS.fetchDepartmentsByOrganizationId}/${orgId}`;
        getData(deptUrl)
            .then(res => {
                if (res.status === 'SUCCESS') setDepartments(res.message?.departments || res.result || []);
            })
            .catch(() => setDepartments([]));
    }, [orgId]);

    const deptId = dept === 'All' ? '' : (departments.find(d => d.department === dept)?.id ?? '');

    const fetchWeeklySummary = useCallback(async () => {
        setWeeklyLoading(true);
        try {
            const response = await axiosInstance.get(API_ENDPOINTS.getWeeklyEmployeeSummary(year, month, deptId));
            setWeeklyData(response.data?.result?.data ?? null);
        } catch (err) {
            console.error('Error fetching weekly summary:', err);
            setWeeklyData(null);
        } finally {
            setWeeklyLoading(false);
        }
    }, [year, month, deptId]);

    useEffect(() => { fetchWeeklySummary(); }, [fetchWeeklySummary, refreshKey]);

    const filteredEmployees = (weeklyData?.employees ?? []).filter(e => {
        const term = search.trim().toLowerCase();
        if (!term) return true;
        return e.employeeName.toLowerCase().includes(term) || (e.empId ?? '').toLowerCase().includes(term);
    });

    const handleExportWeekly = () => {
        if (!weeklyData?.employees?.length) return;
        const rows = filteredEmployees.map(e => {
            const row = { 'Employee ID': e.empId, 'Employee Name': e.employeeName, Department: e.department };
            weeklyData.weeks.forEach(w => { row[`Week ${w.weekNumber} Approved`] = e.weeklyApproved[w.weekNumber] ?? 0; });
            weeklyData.weeks.forEach(w => { row[`Week ${w.weekNumber} OT`] = e.weeklyOT[w.weekNumber] ?? 0; });
            row['Total Approved'] = e.totalApproved;
            row['Total OT'] = e.totalOT;
            row['Total Billable'] = e.totalBillable;
            row['Total Non-Billable'] = e.totalNonBillable;
            return row;
        });
        const workbook = XLSX.utils.book_new();
        const sheet = XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Weekly Summary');
        const monthLabel = MONTHS.find(m => m.value === month)?.label;
        XLSX.writeFile(workbook, `weekly-summary-${monthLabel}-${year}.xlsx`);
    };

    const handleExport = async () => {
        if (exportFromDate && exportToDate) {
            const diffDays = (new Date(exportToDate) - new Date(exportFromDate)) / (1000 * 60 * 60 * 24);
            if (diffDays > 366) {
                setSnackbar({ open: true, msg: 'Export date range cannot exceed 1 year.', severity: 'error' });
                return;
            }
        }

        setExporting(true);
        try {
            const today = new Date().toISOString().split('T')[0];
            const ext = exportFormat === 'csv' ? 'csv' : 'xlsx';
            const fileName = `timesheets-export-${today}.${ext}`;

            const response = await axiosInstance.post(
                API_ENDPOINTS.exportTimesheets(),
                {
                    from_date:  exportFromDate || null,
                    to_date:    exportToDate   || null,
                    status:     exportStatus,
                    department: exportDept === 'All' ? null : exportDept,
                    format:     exportFormat,
                    mode:       'full',
                },
                { responseType: 'blob' }
            );

            const url = URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url; a.download = fileName; a.click();
            URL.revokeObjectURL(url);
            setSnackbar({ open: true, msg: 'Export downloaded successfully.', severity: 'success' });
        } catch (err) {
            const msg = err.response?.status === 404
                ? 'No timesheets found for the selected filters.'
                : 'Export failed. Please try again.';
            setSnackbar({ open: true, msg, severity: err.response?.status === 404 ? 'warning' : 'error' });
        } finally {
            setExporting(false);
        }
    };

    const selectedMonthLabel = MONTHS.find(m => m.value === month)?.label;

    return (
        <Box>
            {/* Shared filter bar + Employee Weekly Summary — primary, payroll/invoicing input */}
            <Box sx={{ mb: 3, p: 2.5, bgcolor: '#fff', borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <WeekIcon sx={{ color: '#6366f1', fontSize: 22 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>Employee Weekly Summary</Typography>
                            <Typography sx={{ fontSize: 12, color: '#64748b' }}>{selectedMonthLabel} {year} · Approved hours only — for payroll, billing &amp; invoicing</Typography>
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                        <TextField
                            size="small"
                            placeholder="Search employee…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            sx={{ minWidth: 180 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Department</InputLabel>
                            <Select value={dept} label="Department" onChange={e => setDept(e.target.value)}>
                                <MenuItem value="All">All Departments</MenuItem>
                                {departments.map(d => <MenuItem key={d.id} value={d.department}>{d.department}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Month</InputLabel>
                            <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
                                {MONTHS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>Year</InputLabel>
                            <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
                                {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Button variant="contained" startIcon={<ExportIcon />} onClick={handleExportWeekly}
                            disabled={!weeklyData?.employees?.length}
                            sx={{ bgcolor: '#6366f1', fontWeight: 700, '&:hover': { bgcolor: '#4f46e5' } }}>
                            Export
                        </Button>
                    </Stack>
                </Box>

                {weeklyLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#6366f1' }} />
                    </Box>
                ) : !filteredEmployees.length ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>No approved timesheet data for the selected period.</Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 560 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#475569', bgcolor: '#f8fafc' }}>Employee ID</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#475569', bgcolor: '#f8fafc' }}>Employee Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#475569', bgcolor: '#f8fafc' }}>Department</TableCell>
                                    {(weeklyData?.weeks ?? []).map(w => (
                                        <TableCell key={w.weekNumber} align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#475569', bgcolor: '#f8fafc', whiteSpace: 'nowrap' }}>
                                            Week {w.weekNumber}<br />
                                            <Typography component="span" sx={{ fontSize: 10, fontWeight: 500, color: '#94a3b8' }}>{fmtRange(w)}</Typography>
                                        </TableCell>
                                    ))}
                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#475569', bgcolor: '#f8fafc' }}>Total Approved</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#475569', bgcolor: '#f8fafc' }}>Total OT</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#475569', bgcolor: '#f8fafc' }}>Billable</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: '#475569', bgcolor: '#f8fafc' }}>Non-Billable</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredEmployees.map(e => (
                                    <TableRow key={e.userId} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '&:last-child td': { border: 0 } }}>
                                        <TableCell><Typography sx={{ fontSize: 13, color: '#64748b' }}>{e.empId || '—'}</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{e.employeeName}</Typography></TableCell>
                                        <TableCell><Typography sx={{ fontSize: 12, color: '#64748b' }}>{e.department}</Typography></TableCell>
                                        {(weeklyData?.weeks ?? []).map(w => (
                                            <TableCell key={w.weekNumber} align="right">
                                                <Typography sx={{ fontSize: 13 }}>{e.weeklyApproved[w.weekNumber] ?? 0}h</Typography>
                                                {(e.weeklyOT[w.weekNumber] ?? 0) > 0 && (
                                                    <Typography sx={{ fontSize: 11, color: '#d97706' }}>+{e.weeklyOT[w.weekNumber]}h OT</Typography>
                                                )}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right"><Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{e.totalApproved}h</Typography></TableCell>
                                        <TableCell align="right"><Typography sx={{ fontSize: 13, fontWeight: 700, color: '#d97706' }}>{e.totalOT}h</Typography></TableCell>
                                        <TableCell align="right"><Typography sx={{ fontSize: 13, color: '#10b981' }}>{e.totalBillable}h</Typography></TableCell>
                                        <TableCell align="right"><Typography sx={{ fontSize: 13, color: '#64748b' }}>{e.totalNonBillable}h</Typography></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {/* Export Panel */}
            <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <ExportIcon sx={{ color: '#6366f1', fontSize: 22 }} />
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>Export Detailed Timesheet Records</Typography>
                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>Download timesheet data as Excel or CSV (max 1-year range)</Typography>
                    </Box>
                </Box>
                <Alert severity="info" variant="outlined" sx={{ mb: 2, fontSize: 12 }}>
                    Unlike the Weekly Summary above (one row per employee, approved hours only, pivoted by week for a single month),
                    this export gives <strong>one row per employee per week</strong> across any status you pick (Draft, Submitted, Approved,
                    Rejected) and any custom date range — with extra detail like Team, Project Count, Submitted/Approved dates,
                    and employee/manager comments. Use this for audits, record-keeping, or dispute reviews, not for payroll totals.
                </Alert>
                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={2.5} alignItems="flex-end">
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 1 }}>Export Format</Typography>
                        <RadioGroup row value={exportFormat} onChange={e => setExportFormat(e.target.value)}>
                            <FormControlLabel value="excel" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>Excel (.xlsx)</Typography>} />
                            <FormControlLabel value="csv"   control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>CSV (.csv)</Typography>} />
                        </RadioGroup>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <TextField label="From Date" type="date" size="small" fullWidth value={exportFromDate}
                            onChange={e => setExportFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <TextField label="To Date" type="date" size="small" fullWidth value={exportToDate}
                            onChange={e => setExportToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select value={exportStatus} label="Status" onChange={e => setExportStatus(e.target.value)}>
                                {['All', 'Draft', 'Submitted', 'Approved', 'Rejected'].map(s => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Department</InputLabel>
                            <Select value={exportDept} label="Department" onChange={e => setExportDept(e.target.value)}>
                                <MenuItem value="All">All</MenuItem>
                                {departments.map(d => <MenuItem key={d.id} value={d.department}>{d.department}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <Button fullWidth variant="contained" startIcon={<ExportIcon />}
                            onClick={handleExport} disabled={exporting}
                            sx={{ bgcolor: '#6366f1', fontWeight: 700, '&:hover': { bgcolor: '#4f46e5' }, whiteSpace: 'nowrap', px: 3, py: 1 }}>
                            {exporting ? 'Exporting…' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
